import {gemStore} from 'app/gemStore';
import {type ShouldSkipLazyLoadFn, type Undef} from 'app/interface';
import {appEvents} from 'app/utils/appEvents';
import {type LazyInitModuleProps} from 'app/utils/initialize/interface';

/**
 * Lazy init component based on:
 * 1. component element visibility
 * 2. changes in store
 * 3. changes in URL hash
 * @param moduleItem
 * @param lazyInit
 * @param config
 * @param element
 */
export function lazyInitModule({shouldSkipLazyLoad, lazyInit, config, element, moduleName}: LazyInitModuleProps) {
    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    lazyInit();
                }
            });
        },
        {rootMargin: '0px 0px 300px 0px'},
    );

    observer.observe(element);

    const unsubscribeFromStore = subscribeToStore(lazyInit);
    let removeURLChangeListener: Undef<() => void>;
    if (shouldSkipLazyLoad) {
        removeURLChangeListener = listenToURLChange(shouldSkipLazyLoad, config, element, lazyInit);
    }

    element.addEventListener(appEvents.componentInitializationStart, (event) => {
        const customEvent = event as CustomEvent<string>;
        if (customEvent.detail === moduleName) {
            observer.disconnect();
            unsubscribeFromStore();
            removeURLChangeListener?.();
        }
    });
}

/**
 * When user hit print, all lazy components on page should initialize, see js/app/modules/printingHandler/index.ts
 * @param lazyInit
 */
export function subscribeToStore(lazyInit: LazyInitModuleProps['lazyInit']) {
    const unsubscribe = gemStore.subscribe((state, prevState) => {
        if (state.printing && !prevState.printing) {
            lazyInit();
        }
    });

    if (gemStore.getState().printing) {
        lazyInit();
    }

    return unsubscribe;
}

/**
 * When URL hash change, some components might need to initialize,
 * e.g. a link with ID in hash is clicked, which belongs to not yet initialized component
 * @param shouldSkipLazyLoad
 * @param config
 * @param element
 * @param lazyInit
 */
export function listenToURLChange(shouldSkipLazyLoad: ShouldSkipLazyLoadFn, config: any, element: HTMLElement, lazyInit: LazyInitModuleProps['lazyInit']) {
    const onPopState = () => {
        if (!window.location.hash || window.location.hash === '#') {
            return;
        }

        if (shouldSkipLazyLoad({element, config}) !== 'LOAD_LAZY') {
            window.removeEventListener('popstate', onPopState);
            lazyInit();
        }
    };

    window.addEventListener('popstate', onPopState);

    return () => {
        window.removeEventListener('popstate', onPopState);
    };
}
